import React from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, UseFormReturn} from 'react-hook-form';
import {PresentationSettingsFormType} from '../../../../../incase-domain/validators/presentation/presentationSchema';
import {MAX_PRESENTATION_DESCRIPTION_LENGTH} from '../../../../../incase-domain/validators/presentation/description';
import InputWithCharacterCounter from '../../../../../components/slides/highlighted/common/InputWithCharacterCounter';
import ToggleSwitch from '../../../../../incase-domain/components/toggleSwitch/ToggleSwitch';
import NumberInput from '../../../../components/NumberInput';
import Label from '../../../../../incase-domain/components/label/Label';

interface GeneralSettingsProps {
    useForm: UseFormReturn<PresentationSettingsFormType>;
}

function GeneralSettings({useForm}: GeneralSettingsProps): JSX.Element {
    const {t} = useTranslation();
    const {control, formState} = useForm;
    const {errors} = formState;

    return (
        <div className="u-p-m">
            <div className="c-verticalNavigation__header">
                <div className="c-verticalNavigation__header_title">{t('presEditPage.settings.general.title')}</div>
            </div>

            <div className="u-flex u-flex-col u-gap-2">
                <div>
                    <Label>{t('presEditPage.settings.general.name')}</Label>
                    <div className="c-sidepanel__input">
                        <Controller
                            name="presentation.name"
                            control={control}
                            render={({field}) => (
                                <input
                                    id="presentation-name"
                                    type="text"
                                    placeholder={t('presEditPage.settings.general.namePlaceholder')}
                                    {...field}
                                />
                            )}
                        />
                    </div>
                    {errors.presentation?.name?.message && <div className="is-error">{errors.presentation?.name.message}</div>}
                </div>

                <div>
                    <Label>{t('presEditPage.settings.general.description')}</Label>
                    <Controller
                        name="presentation.description"
                        control={control}
                        render={({field: {value, onChange, ...restField}}) => (
                            <InputWithCharacterCounter
                                placeholder={t('presEditPage.settings.general.descriptionPlaceholder')}
                                value={value || ''}
                                onChange={onChange}
                                className="c-sidepanel__input"
                                maxLength={MAX_PRESENTATION_DESCRIPTION_LENGTH}
                                {...restField}
                            />
                        )}
                    />
                    {errors.presentation?.description?.message && <div className="is-error">{errors.presentation.description.message}</div>}
                </div>
            </div>

            <div className="c-settingsItem" key="data-collection-settings">
                <div className="u-flex u-h-between u-v-center u-gap-4">
                    <div className="u-flex u-flex-col">
                        <p className="c-settingsItem__title">{t('presEditPage.settings.changeDataCollection')}</p>
                        <p className="c-settingsItem__description">{t('presEditPage.settings.changeDataCollectionDescription')}</p>
                    </div>
                    <div style={{flex: '1'}} />
                    <div className="c-settingsItem__trailing">
                        <Controller
                            name="presentation.dataCollection"
                            control={control}
                            render={({field: {value, onChange, ...restField}}) => (
                                <ToggleSwitch
                                    color="info"
                                    id="data-collection"
                                    isOn={value || false}
                                    handleToggle={onChange}
                                    {...restField}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="c-settingsItem" key="questions-retry-settings">
                <div className="u-flex u-h-between u-v-center u-gap-4">
                    <div className="u-flex u-flex-col">
                        <p className="c-settingsItem__title">{t('presEditPage.settings.retryQuestion.enable')}</p>
                        <p className="c-settingsItem__description">{t('presEditPage.settings.retryQuestion.enableDescription')}</p>
                    </div>
                    <div style={{flex: '1'}} />
                    <div className="c-settingsItem__trailing">
                        <Controller
                            name="presentation.retryQuestionSettings.retryQuestion"
                            control={control}
                            render={({field: {value, onChange, ...restField}}) => (
                                <ToggleSwitch
                                    color="info"
                                    id="questions-retry-options"
                                    isOn={value || false}
                                    handleToggle={onChange}
                                    {...restField}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="c-settingsItem" key="questions-retry-attempts">
                <div className="u-flex u-h-between u-v-center u-gap-4">
                    <div className="u-flex u-flex-col">
                        <p className="c-settingsItem__title">{t('presEditPage.settings.retryQuestion.attempts')}</p>
                        <p className="c-settingsItem__description">{t('presEditPage.settings.retryQuestion.attemptsDescription')}</p>
                    </div>
                    <div style={{flex: '1'}} />
                    <div className="c-settingsItem__trailing">
                        <Controller
                            name="presentation.retryQuestionSettings.noOfAttempts"
                            control={control}
                            render={({field: {value, onChange, ...restField}}) => (
                                <div>
                                    <NumberInput min={2} max={10} value={value || 2} onChange={onChange} {...restField} />
                                </div>
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GeneralSettings;
